package fyi.moko.hotpl.domain.repository

import fyi.moko.hotpl.domain.model.YellowPageEntry
import kotlinx.coroutines.flow.Flow

/**
 * Repository for the public Yellow Pages directory.
 * Orchestrates remote region sync into the local offline cache and exposes
 * offline-first query methods.
 */
interface YellowPageRepository {
    /**
     * Pulls the latest entries for [regionKey] from the remote directory and
     * replaces the local cache slice for that region atomically.
     */
    suspend fun syncRegion(regionKey: String): Result<Unit>

    /** Emits the cached entries for [regionKey] whenever the local cache changes. */
    fun observeCachedEntries(regionKey: String): Flow<List<YellowPageEntry>>

    /**
     * Offline prefix search over the cached directory. The caller should normalize
     * the prefix before calling; results are returned alphabetically by normalized name.
     */
    suspend fun searchLocal(prefix: String, limit: Int = 20): List<YellowPageEntry>

    /**
     * Submits a user-contributed directory entry for remote moderation.
     * The entry is persisted with [YellowPageEntry.STATUS_PENDING] and will not
     * appear in local caches until it transitions to [YellowPageEntry.STATUS_APPROVED].
     */
    suspend fun submitPendingEntry(entry: YellowPageEntry): Result<Unit>

    /**
     * Upserts [entries] straight into the local cache, bypassing the network — for bundled
     * bootstrap/seed content (e.g. [fyi.moko.hotpl.data.seed.KoreanLaPlacesSeed]) used while
     * the remote directory isn't implemented yet. Idempotent: entry ids are deterministic
     * (`{geohash}_{normalizedName}`), so calling this repeatedly with the same seed is a no-op.
     * A later real [syncRegion] for the same region supersedes these rows normally.
     */
    suspend fun seedLocalEntries(entries: List<YellowPageEntry>)
}
